/* eslint-disable react/jsx-indent */
/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useRef, useState } from 'react';
import { useCallback, useEffect } from 'react';
import { FiClipboard } from 'react-icons/fi';
import { useDispatch, useSelector } from 'react-redux';

import { Skeleton } from '@material-ui/lab';
import { FormHandles } from '@unform/core';
import moment from 'moment';

import Button from '~/components/Button';
import Modal from '~/components/Modal';
import OverflowText from '~/components/OverflowText';
import { useAuth } from '~/hooks/auth';
import { IState } from '~/store';
import {
  generateReportRequest,
  getGeneratedReportsRequest,
  getReportsRequest,
} from '~/store/modules/reports/actions';
import { IReport } from '~/store/modules/reports/types';
import { ObjectLiteral } from '~/types/generics';

import HistoryItem from './HistoryItem';
import ReportForm, { ReportFormData } from './ReportForm';
import {
  Container,
  Content,
  Card,
  ReportButtons,
  ReportDescription,
  ReportTitle,
  ButtonContainer,
  SkeletonContainer,
  ModalWrapper,
  ModalCards,
  SkeletonCardContainer,
  NoReportsAvailable,
} from './styles';

const Reports: React.FC = (): JSX.Element => {
  const { user } = useAuth();
  const [showGenerateModal, setShowGenerateModal] = useState<boolean>(false);
  const [showGenerateHistoryModal, setShowGenerateHistoryModal] = useState<
    boolean
  >(false);
  const [canGenerateReport, setCanGenerateReport] = useState<boolean>(false);
  const [selectedReport, setSelectedReport] = useState<IReport | null>(null);
  const [reportsLimit, setReportsLimit] = useState<number>(5);
  const [generatedReportsLimit, setGeneratedReportsLimit] = useState<number>(5);
  const [reportsPage, setReportsPage] = useState<number>(1);
  const [generatedReportsPage, setGeneratedReportsPage] = useState<number>(1);
  const [customFilters, setCustomFilters] = useState<ObjectLiteral>({});
  const [validCustomFilters, setValidCustomFilters] = useState<ObjectLiteral[]>(
    [],
  );

  const loading = useSelector((state: IState) => state.reports.loading);
  const reports = useSelector((state: IState) => state.reports.reports);
  const loadingGeneratedReports = useSelector(
    (state: IState) => state.reports.loadingGeneratedReports,
  );
  const loadingGenerateReport = useSelector(
    (state: IState) => state.reports.loadingGenerateReport,
  );

  const generatedReports = useSelector(
    (state: IState) => state.reports.generatedReports,
  );

  const dispatch = useDispatch();

  const formTableRef = useRef<FormHandles>(null);

  const getWedoProfile = () => {
    const wedoProfile = user.profiles.find((profile) =>
      profile.profileKey.startsWith('WEDO'),
    );

    return wedoProfile;
  };

  const loadReports = useCallback(
    (page: number, limit: number) => {
      const wedoProfile = getWedoProfile();
      const offset = (page - 1) * limit;

      if (wedoProfile) {
        dispatch(
          getReportsRequest({
            offset,
            limit,
            organizationId: user.orgId,
            profileId: wedoProfile.id,
          }),
        );
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dispatch],
  );

  useEffect(() => {
    loadReports(reportsPage, reportsLimit);
  }, [loadReports, reportsLimit, reportsPage]);

  const openGenerateReportModal = (report: IReport) => {
    setSelectedReport(report);
    setShowGenerateModal(true);
  };

  const generateReport = (data: ReportFormData) => {
    const { dateRange } = data;

    const formattedDate = {
      dataCriacao: [dateRange[0].startDate, dateRange[0].endDate],
    };

    setCustomFilters(formattedDate);

    if (selectedReport) {
      dispatch(
        generateReportRequest({
          customFilters: JSON.stringify(formattedDate),
          organizationId: user.orgId,
          reportId: selectedReport.id,
        }),
      );
      setShowGenerateModal(false);
    }
  };

  const loadGeneratedReports = useCallback(
    (report: IReport, page: number, limit: number) => {
      dispatch(
        getGeneratedReportsRequest(report.id, {
          offset: (page - 1) * limit,
          limit,
        }),
      );
      setSelectedReport(report);
      setShowGenerateHistoryModal(true);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dispatch],
  );

  useEffect(() => {
    if (selectedReport?.reportFilters.length === validCustomFilters.length) {
      setCanGenerateReport(true);
    } else {
      setCanGenerateReport(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [validCustomFilters]);

  return (
    <Container>
      <Content>
        {!loading
          ? reports.map((report: IReport) => (
              <Card>
                <OverflowText
                  title={report.name}
                  lines={1}
                  style={{ maxWidth: '272px' }}
                  isInteractive={false}
                >
                  <ReportTitle>{report.name}</ReportTitle>
                </OverflowText>
                <OverflowText
                  title={report.description}
                  lines={3}
                  style={{ maxWidth: '272px' }}
                  isInteractive={false}
                >
                  <ReportDescription>{report.description}</ReportDescription>
                </OverflowText>

                <ReportButtons>
                  <Button onClick={() => openGenerateReportModal(report)}>
                    Gerar
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => {
                      loadGeneratedReports(
                        report,
                        generatedReportsPage,
                        generatedReportsLimit,
                      );
                    }}
                  >
                    Acessar histórico
                  </Button>
                </ReportButtons>
              </Card>
            ))
          : Array.from(Array(4)).map((value, index) => (
              <SkeletonContainer key={`skeleton-${index}`}>
                <Card>
                  <Skeleton
                    className="SkeletonChild"
                    animation="wave"
                    height={21}
                    width={151}
                    style={{ background: '#e4e0e0' }}
                  />
                  <Skeleton
                    className="SkeletonChild"
                    animation="wave"
                    height={67}
                    width={256}
                    style={{ background: '#eeecec' }}
                  />
                  <ReportButtons>
                    <Skeleton
                      className="SkeletonChild"
                      animation="wave"
                      height={50}
                      width={103}
                      style={{ background: '#b3b3b3' }}
                    />
                    <Skeleton
                      className="SkeletonChild"
                      animation="wave"
                      height={50}
                      width={161}
                      style={{
                        background: '#fff',
                        border: '1px solid #b3b3b3',
                      }}
                    />
                  </ReportButtons>
                </Card>
              </SkeletonContainer>
            ))}
      </Content>

      {showGenerateModal && !loadingGenerateReport && (
        <Modal
          title="Defina o período do relatório."
          open={showGenerateModal}
          modalState={(opened) => setShowGenerateModal(opened)}
          disableScrollBar
        >
          <ReportForm
            ref={formTableRef}
            selectedReport={selectedReport}
            onSubmit={generateReport}
          />
        </Modal>
      )}
      <ModalWrapper>
        {showGenerateHistoryModal && !loadingGenerateReport && (
          <Modal
            title="Histórico"
            subtitle={selectedReport?.name}
            description="Atenção! Serão armazenados os últimos 5 relatórios gerados."
            open={showGenerateHistoryModal}
            modalState={(opened) => setShowGenerateHistoryModal(opened)}
          >
            {!loadingGeneratedReports && generatedReports.length === 0 && (
              <NoReportsAvailable>
                <div>
                  <FiClipboard size="60" color="#6c6c6c" />
                </div>
                <p
                  style={{
                    fontSize: '16px',
                    color: '#4d4d4d',
                    padding: '10px',
                  }}
                >
                  Nenhuma histórico de relatório disponível.
                </p>
                <span style={{ color: '#808080' }}>
                  O sistema ainda não possui histórico de relatório disponível.
                  Contate o administrador.
                </span>
              </NoReportsAvailable>
            )}
            {!loadingGeneratedReports ? (
              <ModalCards>
                {generatedReports.map((generatedReport) => (
                  <HistoryItem
                    creationRange={
                      JSON.parse(generatedReport.customFilters).dataCriacao && {
                        start: moment(
                          JSON.parse(generatedReport.customFilters)
                            .dataCriacao[0],
                        ).toDate(),
                        finish: moment(
                          JSON.parse(generatedReport.customFilters)
                            .dataCriacao[1],
                        ).toDate(),
                      }
                    }
                    generatedAt={moment(
                      generatedReport.creationDate,
                      'DD/MM/YYYY HH:mm',
                    ).toDate()}
                    reportId={generatedReport.report.id}
                    reportToken={generatedReport.reportToken}
                    status={generatedReport.status}
                  />
                ))}
              </ModalCards>
            ) : (
              Array.from(Array(3)).map((value, index) => (
                <SkeletonCardContainer>
                  <div>
                    <Skeleton
                      className="SkeletonChild"
                      animation="wave"
                      height={24}
                      width={234}
                      style={{ background: '#e4e0e0' }}
                    />

                    <Skeleton
                      className="SkeletonChild"
                      animation="wave"
                      height={19}
                      width={262}
                      style={{ background: '#e4e0e0' }}
                    />
                  </div>
                  <div style={{ alignSelf: 'center' }}>
                    <Skeleton
                      className="SkeletonChild"
                      animation="wave"
                      height={28}
                      width={18}
                      style={{ background: '#e4e0e0' }}
                    />
                  </div>
                </SkeletonCardContainer>
              ))
            )}
          </Modal>
        )}
      </ModalWrapper>
    </Container>
  );
};

export default Reports;
